import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Metric = 'volume' | 'tc_volume' | 'ullage' | 'height' | 'water_height' | 'temperature';
type Range = '1h' | '24h' | '7d' | '30d';

const METRIC_LABEL: Record<Metric, string> = {
  volume: 'Volume (gal)',
  tc_volume: 'TC Volume (gal)',
  ullage: 'Ullage (gal)',
  height: 'Height (in)',
  water_height: 'Water Height (in)',
  temperature: 'Temperature (°F)',
};
const RANGE_LABEL: Record<Range, string> = {
  '1h': 'Last hour', '24h': 'Last 24 hours', '7d': 'Last 7 days', '30d': 'Last 30 days',
};

// Tank line colors — distinguishable in both light & dark theme.
const TANK_COLORS = ['#2e7df6', '#e0853d', '#28a76f', '#c44dd6', '#d63b5b', '#7a4dd6'];

export function SiteDetail() {
  const { siteId } = useParams();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'operator';
  const [sites, setSites] = useState<any[]>([]);
  const site = sites.find(s => s.id === siteId);

  // ATG that is currently being edited (id) and the in-progress form values.
  const [editingAtg, setEditingAtg] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ label: string; host: string; port: number; pollIntervalS: number; sensorPollIntervalS: number }>({
    label: '', host: '', port: 10001, pollIntervalS: 300, sensorPollIntervalS: 300,
  });
  const [editErr, setEditErr] = useState('');
  const [saving, setSaving] = useState(false);

  const [label, setLabel] = useState('Console 1');
  const [host, setHost] = useState('10.10.10.10');
  const [port, setPort] = useState(10001);
  const [secCode, setSecCode] = useState('');
  const [pollInt, setPollInt] = useState(300);
  const [latestByAtg, setLatestByAtg] = useState<Record<string, any[]>>({});
  const [history, setHistory] = useState<Record<string, any[]>>({});
  const [sensorsByAtg, setSensorsByAtg] = useState<Record<string, any[]>>({});
  const [deliveriesByAtg, setDeliveriesByAtg] = useState<Record<string, any[]>>({});
  const [eventsByAtg, setEventsByAtg] = useState<Record<string, any[]>>({});

  // Per-ATG chart state (metric/range + fetched series)
  const [chartCfg, setChartCfg] = useState<Record<string, { metric: Metric; range: Range }>>({});
  const [seriesByAtg, setSeriesByAtg] = useState<Record<string, any>>({});

  async function refresh() {
    const all = await api.get('/api/sites');
    setSites(all);
    const s = all.find((x: any) => x.id === siteId);
    if (s) {
      const latest: Record<string, any[]> = {};
      const hist: Record<string, any[]> = {};
      const sensors: Record<string, any[]> = {};
      const deliveries: Record<string, any[]> = {};
      const events: Record<string, any[]> = {};
      // Last 7 days window for the inline summary cards.
      const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const until = new Date().toISOString();
      for (const a of s.atgs) {
        latest[a.id] = await api.get(`/api/atgs/${a.id}/latest`);
        hist[a.id] = await api.get(`/api/atgs/${a.id}/history`);
        sensors[a.id] = await api.get(`/api/atgs/${a.id}/sensors/latest`);
        deliveries[a.id] = await api.get(`/api/atgs/${a.id}/deliveries?from=${encodeURIComponent(since)}&to=${encodeURIComponent(until)}`);
        events[a.id] = await api.get(`/api/atgs/${a.id}/sensor-events?from=${encodeURIComponent(since)}&to=${encodeURIComponent(until)}`);
      }
      setLatestByAtg(latest); setHistory(hist); setSensorsByAtg(sensors);
      setDeliveriesByAtg(deliveries); setEventsByAtg(events);
      // Fetch initial timeseries for each ATG with default metric/range.
      for (const a of s.atgs) {
        const cfg = chartCfg[a.id] ?? { metric: 'volume' as Metric, range: '24h' as Range };
        const data = await api.get(`/api/atgs/${a.id}/timeseries?metric=${cfg.metric}&range=${cfg.range}`);
        setSeriesByAtg(prev => ({ ...prev, [a.id]: data }));
        if (!chartCfg[a.id]) setChartCfg(prev => ({ ...prev, [a.id]: cfg }));
      }
    }
  }
  useEffect(() => { refresh().catch(() => {}); }, [siteId]);

  async function setChart(atgId: string, patch: Partial<{ metric: Metric; range: Range }>) {
    const cur = chartCfg[atgId] ?? { metric: 'volume' as Metric, range: '24h' as Range };
    const next = { ...cur, ...patch };
    setChartCfg(prev => ({ ...prev, [atgId]: next }));
    const data = await api.get(`/api/atgs/${atgId}/timeseries?metric=${next.metric}&range=${next.range}`);
    setSeriesByAtg(prev => ({ ...prev, [atgId]: data }));
  }

  async function addAtg() {
    await api.post(`/api/sites/${siteId}/atgs`, {
      label, host, port: Number(port),
      securityCode: secCode || undefined,
      pollIntervalS: Number(pollInt),
    });
    setLabel('Console 1'); setHost(''); setSecCode('');
    await refresh();
  }
  async function pollNow(id: string) {
    await api.post(`/api/atgs/${id}/poll`, { functionCode: 'i20100' });
    await refresh();
  }
  async function refreshLabels(id: string) {
    await api.post(`/api/atgs/${id}/refresh-labels`, {});
    await refresh();
  }

  function startEdit(a: any) {
    setEditingAtg(a.id);
    setEditErr('');
    setEditForm({
      label: a.label,
      host: a.host,
      port: a.port,
      pollIntervalS: a.pollIntervalS ?? a.poll_interval_s ?? 300,
      sensorPollIntervalS: a.sensorPollIntervalS ?? a.sensor_poll_interval_s ?? 300,
    });
  }
  function cancelEdit() { setEditingAtg(null); setEditErr(''); }
  async function saveEdit(atgId: string) {
    // Minimal client-side validation; the server also enforces these ranges.
    if (!editForm.host.trim()) { setEditErr('Host is required.'); return; }
    if (!Number.isInteger(editForm.port) || editForm.port < 1 || editForm.port > 65535) {
      setEditErr('Port must be between 1 and 65535.'); return;
    }
    if (editForm.pollIntervalS < 30 || editForm.pollIntervalS > 86400) {
      setEditErr('Inventory poll interval must be 30–86400 seconds.'); return;
    }
    if (editForm.sensorPollIntervalS < 30 || editForm.sensorPollIntervalS > 86400) {
      setEditErr('Sensor poll interval must be 30–86400 seconds.'); return;
    }
    setSaving(true); setEditErr('');
    try {
      await api.patch(`/api/atgs/${atgId}`, {
        label: editForm.label,
        host: editForm.host.trim(),
        port: editForm.port,
        pollIntervalS: editForm.pollIntervalS,
        sensorPollIntervalS: editForm.sensorPollIntervalS,
      });
      setEditingAtg(null);
      await refresh();
    } catch (e: any) {
      setEditErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!site) return <div>Loading site…</div>;
  return (
    <>
      <div className="card">
        <h2>{site.name}</h2>
        <div style={{ color: 'var(--muted)' }}>{site.customer} · {site.address}</div>
      </div>

      <div className="card">
        <h2>Add ATG console</h2>
        <div className="row">
          <div className="col"><label>Label</label><input value={label} onChange={e => setLabel(e.target.value)} /></div>
          <div className="col"><label>Host (via WireGuard)</label><input value={host} onChange={e => setHost(e.target.value)} /></div>
          <div className="col"><label>TCP port</label><input type="number" value={port} onChange={e => setPort(Number(e.target.value))} /></div>
          <div className="col"><label>RS-232 security code (optional)</label><input value={secCode} onChange={e => setSecCode(e.target.value)} placeholder="6 digits" /></div>
          <div className="col"><label>Poll interval (s)</label><input type="number" value={pollInt} onChange={e => setPollInt(Number(e.target.value))} /></div>
        </div>
        <div style={{ marginTop: 12 }}><button onClick={addAtg}>Add ATG</button></div>
      </div>

      {site.atgs.map((a: any) => {
        const cfg = chartCfg[a.id] ?? { metric: 'volume' as Metric, range: '24h' as Range };
        const series = seriesByAtg[a.id];
        return (
          <div className="card" key={a.id}>
            <h2>{a.label} <span className="pill">{a.host}:{a.port}</span></h2>
            <div style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
              <button onClick={() => pollNow(a.id)}>Poll i20100 now</button>
              <button onClick={() => refreshLabels(a.id)}>Refresh labels</button>
              {canEdit && editingAtg !== a.id && (
                <button onClick={() => startEdit(a)}>Edit connection</button>
              )}
            </div>

            {editingAtg === a.id && (
              <div style={{ padding: 12, background: 'rgba(46, 125, 246, 0.08)', borderRadius: 4, marginBottom: 16 }}>
                <h3 style={{ marginTop: 0 }}>Edit ATG connection</h3>
                <div className="row">
                  <div className="col">
                    <label>Label</label>
                    <input value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))} />
                  </div>
                  <div className="col">
                    <label>Host (IP via WireGuard)</label>
                    <input value={editForm.host} onChange={e => setEditForm(f => ({ ...f, host: e.target.value }))} placeholder="10.10.10.10" />
                  </div>
                  <div className="col">
                    <label>TCP port</label>
                    <input type="number" min={1} max={65535} value={editForm.port}
                           onChange={e => setEditForm(f => ({ ...f, port: Number(e.target.value) }))} />
                  </div>
                  <div className="col">
                    <label>Inventory poll (s)</label>
                    <input type="number" min={30} max={86400} value={editForm.pollIntervalS}
                           onChange={e => setEditForm(f => ({ ...f, pollIntervalS: Number(e.target.value) }))} />
                  </div>
                  <div className="col">
                    <label>Sensor poll (s)</label>
                    <input type="number" min={30} max={86400} value={editForm.sensorPollIntervalS}
                           onChange={e => setEditForm(f => ({ ...f, sensorPollIntervalS: Number(e.target.value) }))} />
                  </div>
                </div>
                {editErr && <div style={{ color: 'crimson', marginTop: 8 }}>{editErr}</div>}
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  <button onClick={() => saveEdit(a.id)} disabled={saving}
                          style={{ background: '#2e7df6', color: '#fff' }}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                  Changes apply on the next scheduler tick (within 60s). No restart needed.
                </div>
              </div>
            )}

            <h3>Latest tank readings</h3>
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Tank</th><th>Name</th><th>Product</th><th>Volume</th><th>TC Vol</th>
                  <th>Ullage</th><th>Height</th><th>Water</th><th>Temp</th><th>Collected</th>
                </tr>
              </thead>
              <tbody>
                {(latestByAtg[a.id] || []).map((r: any) => (
                  <tr key={r.id}>
                    <td>{r.tank_number}</td>
                    <td>{r.tank_label || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td>{r.product_code}</td>
                    <td>{r.volume?.toFixed?.(1)}</td>
                    <td>{r.tc_volume?.toFixed?.(1)}</td>
                    <td>{r.ullage?.toFixed?.(1)}</td>
                    <td>{r.height?.toFixed?.(2)}</td>
                    <td>{r.water_height?.toFixed?.(2)}</td>
                    <td>{r.temperature?.toFixed?.(1)}</td>
                    <td>{new Date(r.collected_at).toLocaleString()}</td>
                  </tr>
                ))}
                {!(latestByAtg[a.id] || []).length && (
                  <tr><td colSpan={10} style={{ color: 'var(--muted)' }}>No readings yet — try Poll now.</td></tr>
                )}
              </tbody>
            </table></div>

            <h3 style={{ marginTop: 24 }}>Trend</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                Metric:
                <select value={cfg.metric} onChange={e => setChart(a.id, { metric: e.target.value as Metric })}>
                  {(Object.keys(METRIC_LABEL) as Metric[]).map(m =>
                    <option key={m} value={m}>{METRIC_LABEL[m]}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                Range:
                <select value={cfg.range} onChange={e => setChart(a.id, { range: e.target.value as Range })}>
                  {(Object.keys(RANGE_LABEL) as Range[]).map(r =>
                    <option key={r} value={r}>{RANGE_LABEL[r]}</option>)}
                </select>
              </label>
            </div>
            <TrendChart
              series={series}
              range={cfg.range}
              tankLabels={Object.fromEntries((latestByAtg[a.id] || []).map((r: any) => [r.tank_number, r.tank_label || `Tank ${r.tank_number}`]))}
            />

            <h3 style={{ marginTop: 24 }}>Sensor status</h3>
            <div className="table-wrap"><table>
              <thead>
                <tr><th>Sensor</th><th>Location</th><th>Status</th><th>Last polled</th></tr>
              </thead>
              <tbody>
                {(sensorsByAtg[a.id] || []).map((s: any) => (
                  <tr key={s.sensor_number}>
                    <td>{s.sensor_number}</td>
                    <td>{s.sensor_label || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td>
                      <span className={`pill ${s.status_code === 0 ? 'ok' : 'err'}`}>
                        {s.status_label}
                      </span>
                    </td>
                    <td>{new Date(s.collected_at).toLocaleString()}</td>
                  </tr>
                ))}
                {!(sensorsByAtg[a.id] || []).length && (
                  <tr><td colSpan={4} style={{ color: 'var(--muted)' }}>No sensor readings yet — wait for next sensor poll or use Poll now after deploying.</td></tr>
                )}
              </tbody>
            </table></div>

            <h3 style={{ marginTop: 24 }}>Recent deliveries <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 'normal' }}>(last 7 days)</span></h3>
            <div className="table-wrap"><table>
              <thead>
                <tr><th>Tank</th><th>Name</th><th>Started</th><th>Ended</th><th>Volume (gal)</th><th>TC Volume (gal)</th></tr>
              </thead>
              <tbody>
                {(deliveriesByAtg[a.id] || []).slice(0, 10).map((d: any) => (
                  <tr key={d.id}>
                    <td>{d.tank_number}</td>
                    <td>{d.tank_label || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td>{new Date(d.started_at).toLocaleString()}</td>
                    <td>{new Date(d.ended_at).toLocaleString()}</td>
                    <td>{Number(d.amount_volume).toFixed(1)}</td>
                    <td>{d.amount_tc_volume != null ? Number(d.amount_tc_volume).toFixed(1) : '—'}</td>
                  </tr>
                ))}
                {!(deliveriesByAtg[a.id] || []).length && (
                  <tr><td colSpan={6} style={{ color: 'var(--muted)' }}>No deliveries in the last 7 days. See Reports for longer ranges.</td></tr>
                )}
              </tbody>
            </table></div>

            <h3 style={{ marginTop: 24 }}>Recent sensor events <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 'normal' }}>(last 7 days)</span></h3>
            <div className="table-wrap"><table>
              <thead>
                <tr><th>When</th><th>Sensor</th><th>Location</th><th>Previous</th><th>New status</th></tr>
              </thead>
              <tbody>
                {(eventsByAtg[a.id] || []).slice(0, 10).map((ev: any, idx: number) => (
                  <tr key={idx}>
                    <td>{new Date(ev.collected_at).toLocaleString()}</td>
                    <td>{ev.sensor_number}</td>
                    <td>{ev.sensor_label || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td>{ev.prev_status_label || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td>
                      <span className={`pill ${ev.status_code === 0 ? 'ok' : 'err'}`}>{ev.status_label}</span>
                    </td>
                  </tr>
                ))}
                {!(eventsByAtg[a.id] || []).length && (
                  <tr><td colSpan={5} style={{ color: 'var(--muted)' }}>No sensor state changes in the last 7 days.</td></tr>
                )}
              </tbody>
            </table></div>

            <h3 style={{ marginTop: 24 }}>Recent polls</h3>
            <div className="table-wrap"><table>
              <thead><tr><th>When</th><th>Function</th><th>Result</th><th>Duration</th><th>Error</th></tr></thead>
              <tbody>
                {(history[a.id] || []).slice(0, 10).map((h: any) => (
                  <tr key={h.id}>
                    <td>{new Date(h.collected_at).toLocaleString()}</td>
                    <td>{h.function_code}</td>
                    <td>{h.ok ? <span className="pill ok">OK</span> : <span className="pill err">FAIL</span>}</td>
                    <td>{h.duration_ms ?? '—'} ms</td>
                    <td>{h.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        );
      })}
    </>
  );
}

/**
 * Lightweight inline-SVG line chart. One line per tank.
 * Avoids pulling in a chart library; perfectly adequate for a handful of tanks.
 *
 * The X-axis spans the FULL requested range (not just the range of returned
 * points), so switching from 24h → 7d → 30d visibly changes the chart even
 * when only a few hours of data has been collected so far. Tick formatting
 * also follows the requested range so '7d' / '30d' always shows date labels,
 * never hours.
 */
function TrendChart({
  series, range, tankLabels,
}: { series: any; range: Range; tankLabels: Record<number, string> }) {
  if (!series || !series.points || series.points.length === 0) {
    return <div style={{ color: 'var(--muted)', padding: '12px 0' }}>No data in this range yet.</div>;
  }
  // Group by tank
  const byTank: Record<number, Array<{ t: number; v: number }>> = {};
  for (const p of series.points) {
    const t = new Date(p.collected_at).getTime();
    const v = Number(p.value);
    if (!Number.isFinite(v)) continue;
    (byTank[p.tank_number] ??= []).push({ t, v });
  }
  const tanks = Object.keys(byTank).map(Number).sort((a, b) => a - b);
  if (tanks.length === 0) {
    return <div style={{ color: 'var(--muted)', padding: '12px 0' }}>No numeric data in this range yet.</div>;
  }
  const all = tanks.flatMap(t => byTank[t]);

  // X domain = the REQUESTED window, anchored to "now".
  // This way picking '7d' always shows a 7-day axis, even if only a few hours
  // of data exist — you see a short cluster on the right with mostly-empty
  // axis on the left, which is the truthful picture.
  const rangeMs = RANGE_MS[range];
  const tMax = Date.now();
  const tMin = tMax - rangeMs;
  const tRange = tMax - tMin;

  const vMin = Math.min(...all.map(p => p.v));
  const vMax = Math.max(...all.map(p => p.v));
  const vRange = vMax - vMin || 1;

  const W = 820, H = 280;
  const m = { top: 12, right: 16, bottom: 32, left: 56 };
  const innerW = W - m.left - m.right;
  const innerH = H - m.top - m.bottom;
  const x = (t: number) => m.left + ((t - tMin) / tRange) * innerW;
  const y = (v: number) => m.top + innerH - ((v - vMin) / vRange) * innerH;

  const yTicks = Array.from({ length: 5 }, (_, i) => vMin + (vRange * i) / 4);
  // X tick count + label formatter follow the REQUESTED range, not the data.
  const { ticks: xTicks, format: xFmt } = xAxisTicks(tMin, tMax, range);

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMinYMin meet"
           style={{ background: 'var(--card-bg, #fff)', borderRadius: 6 }}>
        <line x1={m.left} y1={m.top} x2={m.left} y2={m.top + innerH} stroke="#888" />
        <line x1={m.left} y1={m.top + innerH} x2={m.left + innerW} y2={m.top + innerH} stroke="#888" />
        {yTicks.map((v, i) => (
          <g key={'y' + i}>
            <line x1={m.left} y1={y(v)} x2={m.left + innerW} y2={y(v)} stroke="#ddd" strokeDasharray="2 3" />
            <text x={m.left - 6} y={y(v) + 4} fontSize="11" textAnchor="end" fill="#666">
              {v.toFixed(v >= 100 ? 0 : 2)}
            </text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <g key={'x' + i}>
            <line x1={x(t)} y1={m.top} x2={x(t)} y2={m.top + innerH} stroke="#eee" strokeDasharray="2 3" />
            <text x={x(t)} y={m.top + innerH + 18} fontSize="11" textAnchor="middle" fill="#666">
              {xFmt(t)}
            </text>
          </g>
        ))}
        {tanks.map((tankNum, idx) => {
          // Clip points to the visible window so old samples outside the requested
          // range (shouldn't happen since the server filters, but be defensive)
          // don't pull the line off-screen.
          const pts = byTank[tankNum]
            .filter(p => p.t >= tMin && p.t <= tMax)
            .sort((a, b) => a.t - b.t);
          if (pts.length === 0) return null;
          const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.t).toFixed(2)} ${y(p.v).toFixed(2)}`).join(' ');
          return <path key={tankNum} d={d} fill="none" stroke={TANK_COLORS[idx % TANK_COLORS.length]} strokeWidth={2} />;
        })}
      </svg>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6, fontSize: 13 }}>
        {tanks.map((tankNum, idx) => (
          <span key={tankNum} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 12, height: 3, background: TANK_COLORS[idx % TANK_COLORS.length] }} />
            {tankLabels[tankNum] || `Tank ${tankNum}`}
          </span>
        ))}
      </div>
    </div>
  );
}

// Window size in milliseconds per Range key.
const RANGE_MS: Record<Range, number> = {
  '1h':  1 * 3600 * 1000,
  '24h': 24 * 3600 * 1000,
  '7d':  7 * 24 * 3600 * 1000,
  '30d': 30 * 24 * 3600 * 1000,
};

/**
 * Choose tick positions + label formatter based on the requested range.
 * - 1h: 6 ticks at 10-minute boundaries, HH:MM labels
 * - 24h: 6 ticks at 4-hour boundaries, HH:MM labels
 * - 7d: 7 ticks at day boundaries, "Mon 5/26" labels
 * - 30d: 6 ticks at 5-day boundaries, "5/26" labels
 */
function xAxisTicks(tMin: number, tMax: number, range: Range): {
  ticks: number[];
  format: (t: number) => string;
} {
  const fmtTime  = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const fmtDay   = (t: number) => new Date(t).toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' });
  const fmtDate  = (t: number) => new Date(t).toLocaleDateString([], { month: 'numeric', day: 'numeric' });

  function evenly(n: number): number[] {
    return Array.from({ length: n }, (_, i) => tMin + ((tMax - tMin) * i) / (n - 1));
  }

  switch (range) {
    case '1h':  return { ticks: evenly(7),  format: fmtTime };
    case '24h': return { ticks: evenly(7),  format: fmtTime };
    case '7d': {
      // Snap to local midnight boundaries so labels read cleanly as days.
      const ticks: number[] = [];
      const start = new Date(tMin);
      start.setHours(0, 0, 0, 0);
      // Step day-by-day from the first midnight ≥ tMin.
      for (let d = start.getTime(); d <= tMax; d += 24 * 3600 * 1000) {
        if (d >= tMin) ticks.push(d);
      }
      // Always include tMax as the right edge if we didn't already.
      if (ticks.length === 0 || ticks[ticks.length - 1] < tMax - 12 * 3600 * 1000) ticks.push(tMax);
      return { ticks, format: fmtDay };
    }
    case '30d': {
      // 6 evenly-spaced ticks across the 30-day window.
      return { ticks: evenly(6), format: fmtDate };
    }
  }
}
